import { ArrowSquareOutIcon, CircleNotch, GlobeIcon, PencilSimpleIcon, WarningIcon } from '@phosphor-icons/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '~/components/ui/button';
import { AppPreview } from '~/features/apps/app-preview';
import { PUBLISHED_MESSAGE } from '~/features/apps/publish-changes';
import { publishUndoToast } from '~/features/apps/publish-undo';
import { usePublishApp } from '~/hooks/mutations/use-publish-app';
import { APP_DRAFTS_FLAG, APP_DRAFT_PREVIEW_FLAG, useFeatureFlag } from '~/hooks/queries/use-feature-flags';
import { api } from '~/lib/api-client';

export interface AppPublishedOutput {
  ok?: boolean;
  error?: string;
  /** False when the server only saved it: the owner takes it live with Publish. Absent otherwise. */
  live?: boolean;
  note?: string;
  app?: {
    slug?: string;
    title?: string;
    status?: string;
    publishedAt?: string | null;
    path?: string;
    url?: string;
  };
}

interface CurrentAppRow {
  slug: string;
  title: string;
  status: 'draft' | 'live' | 'archived';
  hasContent: boolean;
  publishedAt: string | null;
  updatedAt: string;
  /** The owner's count of changes that aren't live yet; null when unknown. */
  unpublishedChanges?: number | null;
}

export const SAVED_NOT_LIVE = 'Saved, not live yet';

/** A held save that still waits; an unknown count still offers Publish, whose summary says what is there. */
export function heldForPublish(output: Pick<AppPublishedOutput, 'live'>, current: CurrentAppRow | null | undefined): { goingLive: boolean } | null {
  if (output.live !== false || !current) return null;
  if (current.status === 'draft') return { goingLive: true };
  if (current.status !== 'live') return null;
  const waiting = current.unpublishedChanges;
  return waiting == null || waiting > 0 ? { goingLive: false } : null;
}

/** Publish from the chat: the same confirmation of what goes live as the app's Edit page (FRONT-3369). */
function PublishHeldButton({ slug, goingLive }: { slug: string; goingLive: boolean }) {
  const qc = useQueryClient();
  const draftsOn = useFeatureFlag(APP_DRAFTS_FLAG, false);
  const previewFlag = useFeatureFlag(APP_DRAFT_PREVIEW_FLAG, false);
  const previewOn = draftsOn && previewFlag;
  const publish = usePublishApp(
    slug,
    {
      onSuccess: (data) => toast.success(PUBLISHED_MESSAGE, publishUndoToast(qc, slug, data)),
      onError: (e: Error) => toast.error(e.message || 'Could not publish the app'),
    },
    { goingLive, previewOn },
  );
  return (
    <Button
      size="sm"
      className="self-start"
      data-testid="app-published-publish"
      disabled={publish.isPending}
      tooltip={publish.isPending ? 'Publishing…' : 'See what goes live, then make it live for everyone who can open the app'}
      onClick={() => publish.mutate()}
    >
      {publish.isPending ? <CircleNotch size={14} className="animate-spin" /> : null}
      {publish.isPending ? 'Publishing…' : 'Publish'}
    </Button>
  );
}

/**
 * Specialised render for `app_publish_html`. Shows a small live preview of
 * the published app pulled from /apps/<slug>/ alongside the title and a
 * link to open it in a new tab. Falls back to a plain error row if the
 * tool reported a failure.
 */
export function AppPublishedCard({ output }: { output: AppPublishedOutput }) {
  if (output.error || !output.app?.slug) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground px-3 py-2 border border-border rounded-lg">
        <WarningIcon size={14} />
        <span>{output.error ?? 'Publish failed.'}</span>
      </div>
    );
  }

  const { slug, title, url, path } = output.app;

  // The tool output is frozen at the time of the call, so a "LIVE" badge in
  // history can lie: the app may have been deleted or unpublished since.
  // Re-check against the admin endpoint and reconcile.
  const { data, isLoading, error } = useQuery<CurrentAppRow | null>({
    queryKey: ['app-published-card', slug],
    queryFn: async () => {
      try {
        const r = await api.get<{ app: CurrentAppRow }>(`/api/admin/apps/${slug}`);
        return r.app;
      } catch (e) {
        // 404 → deleted; surface as null so we can render the deleted state.
        if ((e as { status?: number }).status === 404) return null;
        throw e;
      }
    },
    staleTime: 30_000,
    retry: false,
  });

  const currentStatus: 'live' | 'draft' | 'archived' | 'deleted' | 'unknown' = isLoading
    ? 'unknown'
    : error
      ? 'unknown'
      : data == null
        ? 'deleted'
        : data.status;
  const isLive = currentStatus === 'live';
  const isDeleted = currentStatus === 'deleted';
  const isDraft = currentStatus === 'draft' || currentStatus === 'archived';
  const held = heldForPublish(output, data);

  const liveLink = url ?? path ?? `/apps/${slug}/`;
  const linkTarget = isLive ? liveLink : `/admin/apps`;
  const displayLabel = isLive ? liveLink : 'Open in Apps admin';

  const badge = (() => {
    if (held) {
      return (
        <span
          data-testid="app-published-held"
          className="ml-auto inline-flex items-center gap-1 text-[length:var(--text-2xs)] uppercase tracking-wide rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-300 border border-amber-500/20 px-1.5 py-0.5"
        >
          <span className="size-1 rounded-full bg-amber-500" /> {SAVED_NOT_LIVE}
        </span>
      );
    }
    if (currentStatus === 'unknown') {
      return (
        <span className="ml-auto inline-flex items-center gap-1 text-[length:var(--text-2xs)] uppercase tracking-wide rounded-full bg-muted text-muted-foreground border border-border px-1.5 py-0.5">
          <span className="size-1 rounded-full bg-muted-foreground" /> Checking…
        </span>
      );
    }
    if (isLive) {
      return (
        <span className="ml-auto inline-flex items-center gap-1 text-[length:var(--text-2xs)] uppercase tracking-wide rounded-full bg-success/15 text-success border border-success/30 px-1.5 py-0.5">
          <span className="size-1 rounded-full bg-success" /> Live
        </span>
      );
    }
    if (isDeleted) {
      return (
        <span className="ml-auto inline-flex items-center gap-1 text-[length:var(--text-2xs)] uppercase tracking-wide rounded-full bg-destructive/10 text-destructive border border-destructive/20 px-1.5 py-0.5">
          <span className="size-1 rounded-full bg-destructive" /> Deleted
        </span>
      );
    }
    return (
      <span className="ml-auto inline-flex items-center gap-1 text-[length:var(--text-2xs)] uppercase tracking-wide rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-300 border border-amber-500/20 px-1.5 py-0.5">
        <span className="size-1 rounded-full bg-amber-500" /> {currentStatus === 'archived' ? 'Archived' : 'Draft'}
      </span>
    );
  })();

  const subline = (() => {
    if (held) return 'Publish shows what goes live first.';
    if (currentStatus === 'unknown') return 'Checking current status…';
    if (isLive) return 'Published at';
    if (isDeleted) return 'This app was deleted — link no longer works.';
    if (isDraft) return currentStatus === 'archived' ? 'Archived — not live anymore.' : 'Saved as draft — not live yet.';
    return '';
  })();

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      <div className="px-3 py-2 flex items-center gap-2 text-xs border-b border-border">
        <GlobeIcon size={14} className="text-muted-foreground shrink-0" />
        <span className={`font-medium truncate ${isDeleted ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
          {data?.title ?? title ?? slug}
        </span>
        {badge}
      </div>
      <div className="p-3 flex gap-3 items-start">
        {isDeleted ? (
          <div className="w-[200px] h-[125px] shrink-0 rounded-md border border-dashed border-border bg-muted/30 flex items-center justify-center text-muted-foreground">
            <WarningIcon size={20} />
          </div>
        ) : (
          <AppPreview
            slug={slug}
            source={isLive ? 'live' : 'admin'}
            width={200}
            height={125}
            cacheKey={data?.updatedAt ?? slug}
            title={data?.title ?? title}
            showChrome
            onOpen={() => isLive && window.open(linkTarget, '_blank', 'noopener')}
            className="shrink-0"
          />
        )}
        <div className="flex-1 min-w-0 text-xs">
          <p className={`mb-1.5 ${isDeleted ? 'text-destructive/80' : 'text-muted-foreground'}`}>
            {subline}
          </p>
          {!isDeleted && (
            <div className="flex flex-col gap-1.5">
              <a
                href={linkTarget}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 font-mono text-foreground hover:text-primary hover:underline break-all"
              >
                {displayLabel}
                <ArrowSquareOutIcon size={11} className="shrink-0" />
              </a>
              <a
                href={`/admin/apps/${slug}`}
                className="inline-flex items-center gap-1 self-start text-muted-foreground hover:text-foreground hover:underline"
              >
                <PencilSimpleIcon size={11} className="shrink-0" />
                Edit in Apps admin
              </a>
              {held && <PublishHeldButton slug={slug} goingLive={held.goingLive} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
